import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Download, Share2, Phone } from "lucide-react";
import { toast } from "sonner";
import { playChime, getMuted } from "@/lib/sound";
import { downloadVerseCard } from "@/lib/shareCard";
import { PRAYER_LINES } from "@/data/site";

export default function VerseModal({ open, verse, onClose }) {
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (open && verse && !getMuted()) {
      playChime();
    }
  }, [open, verse]);

  const handleShare = async () => {
    if (!verse) return;
    setSharing(true);
    try {
      downloadVerseCard(verse);
      toast.success("Verse card saved. Share it with someone on the road.");
    } catch (e) {
      toast.error("Couldn't create the card. Try again in a moment.");
    } finally {
      setTimeout(() => setSharing(false), 800);
    }
  };

  const handleWebShare = async () => {
    if (!verse) return;
    const shareData = {
      title: "Road to Salvation",
      text: `"${verse.text}" — ${verse.reference}`,
      url: typeof window !== "undefined" ? window.location.origin : "",
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* user cancel */
      }
    } else {
      try {
        await navigator.clipboard.writeText(
          `"${verse.text}" — ${verse.reference}\n\n${shareData.url}`,
        );
        toast.success("Verse copied to clipboard.");
      } catch {
        toast.error("Copy failed. Try downloading the card instead.");
      }
    }
  };

  return (
    <AnimatePresence>
      {open && verse ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="verse-modal-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            className="relative z-10 max-w-2xl w-full rts-glass rounded-2xl overflow-hidden"
            style={{
              boxShadow:
                "0 0 60px rgba(234,179,8,0.25), inset 0 0 0 1px rgba(234,179,8,0.25)",
            }}
            initial={{ scale: 0.9, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            data-testid="verse-modal"
          >
            <div className="p-8 md:p-12 relative">
              <span
                className="absolute top-4 right-4 text-[0.68rem] uppercase tracking-[0.28em] text-[#EAB308]/80 font-mono-rts"
                data-testid="verse-type"
              >
                {verse.streak ? `Day ${verse.streak} Streak Verse` : verse.type === "prayer" ? "Prayer" : "Scripture"}
              </span>

              <div className="mb-6 flex items-center gap-3">
                <span
                  className="h-px flex-1 bg-gradient-to-r from-transparent via-[#EAB308]/60 to-transparent"
                  aria-hidden="true"
                />
                <span
                  className="font-mono-rts text-[0.7rem] tracking-[0.35em] uppercase text-[#EAB308]"
                  data-testid="verse-reference"
                >
                  {verse.reference}
                </span>
                <span
                  className="h-px flex-1 bg-gradient-to-r from-transparent via-[#EAB308]/60 to-transparent"
                  aria-hidden="true"
                />
              </div>

              <p
                className="font-serif-rts text-2xl md:text-3xl leading-relaxed font-light text-white text-center"
                data-testid="verse-text"
              >
                &ldquo;{verse.text}&rdquo;
              </p>

              <div className="mt-10 flex flex-col items-center gap-5">
                <button
                  onClick={onClose}
                  className="rts-btn-primary"
                  data-testid="verse-continue-btn"
                  autoFocus
                >
                  Continue the Journey
                </button>

                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <button
                    onClick={handleShare}
                    disabled={sharing}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/70 hover:text-[#EAB308] border-b border-transparent hover:border-[#EAB308] pb-1 transition-colors duration-300 disabled:opacity-60"
                    data-testid="verse-download-btn"
                  >
                    <Download className="w-3.5 h-3.5" /> Download Verse Card
                  </button>
                  <span className="text-white/20">|</span>
                  <button
                    onClick={handleWebShare}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/70 hover:text-[#EAB308] border-b border-transparent hover:border-[#EAB308] pb-1 transition-colors duration-300"
                    data-testid="verse-share-btn"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                  <span className="text-white/20">|</span>
                  <a
                    href="/prayer-line"
                    className="inline-flex items-center text-xs uppercase tracking-[0.22em] text-white/70 hover:text-[#EAB308] border-b border-transparent hover:border-[#EAB308] pb-1 transition-colors duration-300"
                    data-testid="verse-prayerline-link"
                  >
                    Request Prayer
                  </a>
                </div>

                <div className="mt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
                  {PRAYER_LINES.map((line, idx) => (
                    <a
                      key={line.tel}
                      href={line.tel}
                      className="inline-flex items-center gap-3 group"
                      data-testid={idx === 0 ? "verse-phone-link" : "verse-phone-link-crossroads"}
                    >
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#EAB308]/40 text-[#EAB308] group-hover:bg-[#EAB308] group-hover:text-[#05050A] transition-colors duration-300">
                        <Phone className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-left">
                        <span className="block text-[0.62rem] uppercase tracking-[0.3em] text-white/50 font-mono-rts">
                          {line.label}
                        </span>
                        <span className="block font-serif-rts text-white group-hover:text-[#EAB308] transition-colors duration-300">
                          {line.display}
                        </span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
